/**
 * FELRUTAN OCH LADDNINGEN.
 *
 * TEXTEN SKRIVS ALDRIG HÄR. Felen i shared/errors.ts har svenska texter som är
 * skrivna för den arbetande chefen på en telefon i ett trapphus — vad som hände
 * och vad han gör åt det — och de ska visas ORDAGRANT. Skriv aldrig en egen
 * text för ett fel kontraktet redan namngivit; skriv om texten I KONTRAKTET om
 * den är dålig.
 *
 * TVÅ TONER: 'vantar' (--warn) för de fel där ingenting är förlorat och appen
 * eller användaren snart löser det — nätet, SUBSCRIPTION_LOCKED,
 * LICENSE_EXPIRED, TOO_MANY_OPS — och normal (--accent) för de som kräver att
 * någon gör något åt just den här saken. Skillnaden är retry-fältet: 'retry'
 * och 'hold' betyder båda att raden ligger kvar.
 */

import type { ReactNode } from 'react';
import { atgardFor } from '../api';
import type { Klartidfel } from '../api';

export interface Felrutaprops {
  readonly fel: Klartidfel;
  /** En knapp: "Försök igen", "Logga in". */
  readonly atgard?: ReactNode;
  /**
   * Visa felkoden i liten mono under texten. AV som standard — koden är för
   * oss, inte för montören. PÅ i utvecklingsläge och i supportvyer.
   */
  readonly visaKod?: boolean;
}

export const Felruta = ({ fel, atgard, visaKod = false }: Felrutaprops) => {
  const vantar = atgardFor(fel) !== 'give_up';
  return (
    <div className={vantar ? 'k-fel-ruta k-vantar' : 'k-fel-ruta'} role="status">
      <span className="k-fel-strimma" aria-hidden="true" />
      <span className="k-fel-text">
        {fel.text}
        {visaKod && (
          <span className="k-fel-kod">
            {fel.slag === 'api' ? fel.code : fel.slag.toUpperCase()}
          </span>
        )}
        {atgard !== undefined && <span className="k-fel-atgard">{atgard}</span>}
      </span>
    </div>
  );
};

/**
 * 501-rutan. TVÅ av kontraktets rutter svarar så i dag — FILE_UPLOAD och
 * FILE_DOWNLOAD — så den syns på arbetsordern och utläggen. Rösten
 * (VOICE_TRANSCRIBE) är byggd och använder den inte längre. Rutan ska säga
 * sanningen i stället för att låna en felkod som får telefonen att skicka om i
 * evighet. Se api/fel.ts.
 */
export const EjByggd = ({ vad }: { readonly vad: string }) => (
  <div className="k-fel-ruta" role="status">
    <span className="k-fel-strimma" aria-hidden="true" />
    <span className="k-fel-text">
      {vad} är inte byggt än.
      <span className="k-fel-kod">Kommer i en senare version</span>
    </span>
  </div>
);

/** Skelettrader medan något hämtas. Antalet ska likna det som kommer. */
export const Laddar = ({ rader = 3 }: { readonly rader?: number }) => (
  <div className="k-laddar-kort" aria-busy="true" aria-live="polite">
    <span className="dold">Hämtar</span>
    {Array.from({ length: rader }, (_, i) => (
      <div
        className="k-skelett"
        key={i}
        style={{ width: `${100 - i * 12}%`, animationDelay: `${i * 120}ms` }}
      />
    ))}
  </div>
);
